import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type MarketScope = "global" | "pt" | "br";

export const MARKET_SCOPE_LABELS: Record<MarketScope, string> = {
  global: "Global (PT + BR)",
  pt: "Só Portugal",
  br: "Só Brasil",
};

/** Market scope of a user. No row = global (keeps existing users unchanged). */
export function useMarketScope(userId?: string) {
  const { user, userRole } = useAuth();
  const target = userId || user?.id;
  const { data, isLoading } = useQuery({
    queryKey: ["market-scope", target],
    enabled: !!target,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("user_market_scopes")
        .select("scope")
        .eq("user_id", target)
        .maybeSingle();
      if (error) throw error;
      return (data?.scope as MarketScope) || "global";
    },
  });
  const scope: MarketScope = !userId && userRole === "super_admin" ? "global" : data || "global";
  return { scope, isLoading };
}

export function useSetMarketScope() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ userId, scope }: { userId: string; scope: MarketScope }) => {
      const { error } = await (supabase as any)
        .from("user_market_scopes")
        .upsert({ user_id: userId, scope, updated_by: user?.id }, { onConflict: "user_id" });
      if (error) throw error;
    },
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: ["market-scope", v.userId] }),
  });
}
